/**
 * AssigneeChip — the avatar + the name of a task's assignee (FRIDAY-75).
 *
 * The board card and the list view used to show the first characters of the
 * assignee's user id. They now show the same person the rest of the app
 * shows: the avatar (custom avatar, provider avatar, Gravatar or initials)
 * and the display name.
 */

import { pickDisplay, type PublicProfile } from '@/features/profiles/profiles'
import { cn } from '@/lib/utils'
import { UserAvatar } from './UserAvatar'

export function AssigneeChip({
  userId,
  profiles,
  size = 'sm',
  withName = true,
  className,
}: {
  userId: string | null | undefined
  profiles: Record<string, PublicProfile>
  size?: 'xs' | 'sm'
  withName?: boolean
  className?: string
}) {
  const sizeCls = size === 'xs' ? 'h-5 w-5 text-[9px]' : 'h-6 w-6 text-[10px]'
  if (!userId) {
    if (!withName) {
      return (
        <span
          title="Unassigned"
          aria-label="Unassigned"
          className={cn(
            'bg-surface-200 dark:bg-surface-700 text-surface-500 flex items-center justify-center rounded-full',
            sizeCls,
          )}
        >
          —
        </span>
      )
    }
    return (
      <span className={cn('text-surface-500 inline-flex items-center gap-1.5', className)}>
        <span
          className={cn(
            'bg-surface-200 dark:bg-surface-700 flex items-center justify-center rounded-full',
            sizeCls,
          )}
        >
          —
        </span>
        <span className="truncate">Unassigned</span>
      </span>
    )
  }
  const profile = profiles[userId]
  const display = pickDisplay(userId, profile)
  return (
    <span
      className={cn('inline-flex min-w-0 items-center gap-1.5', className)}
      title={display.displayName}
      data-testid="assignee-chip"
    >
      <UserAvatar
        name={display.displayName}
        email={display.email}
        sources={display.avatarSources}
        className={sizeCls}
        pixelSize={48}
        decorative={!withName}
      />
      {withName && (
        <span className="text-surface-700 dark:text-surface-300 truncate">{display.displayName}</span>
      )}
    </span>
  )
}
